// src/pages/HAFHHome.jsx
import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser, faBuilding, faChartLine, faUtensils, faClipboardCheck, faRankingStar, faBook, faUserTie, faUsers, faQrcode, 
  faBell, faGamepad, faBuildingColumns, faBrain, faShieldAlt, faTriangleExclamation, faBus, faUserGraduate, faSchool,
  faPeopleGroup, faSitemap, faChalkboardUser, faClipboardList,
  faNewspaper,
  faCircleInfo,
  faBolt,
  faBookOpen
} from "@fortawesome/free-solid-svg-icons";

import { THEMES } from "../../themes/platformThemes";

import useAttendance from "./useAttendance";
import { students, SEED_EMERGENCIES} from "./data/HAFHSeed";
// import MessMenuManager from "./HAFHMessMenuManager";
import HAFHOwner from "./HAFHOwner";
import HAFHManager from "./HAFHManager";
import HAFHParent from "./HAFHParent";
import HAFHMess from "./HAFHMess";
import HAFHStudent from "./HAFHStudent";
import HAFHAdminPanel from "./HAFHAdminPanel";
import HAFHInstitution from "./HAFHInstitution";
import HAFHTransporter from "./HAFHTransporter";
import HAFHAI from "./HAFHAI";
import HAFHHuma from "./HAFHHuma";
import HAFHDirector from "./HAFHDirector";
import HAFHTrustee from "./HAFHTrustee";
import HAFHCoordinator from "./HAFHCoordinator";
import HAFHOnboarding from "./components/HAFHOnboarding";
import HAFHAbout from "./HAFHAbout";
import HAFHUpdates from "./components/HAFHUpdatesList";

import hafhTranslations from "./HAFHTranslations";   

ChartJS.register(BarElement, CategoryScale, LinearScale, ArcElement, Tooltip, Legend);

const languages = [
  { code: "en", label: "English" }, 
  { code: "hi", label: "हिंदी" },
  { code: "mr", label: "मराठी" }, 
  { code: "gu", label: "ગુજરાતી" },
  { code: "bn", label: "বাংলা" }, 
  { code: "ta", label: "தமிழ்" },
  { code: "te", label: "తెలుగు" }, 
  { code: "kn", label: "ಕನ್ನಡ" },
  { code: "ml", label: "മലയാളം" }, 
  { code: "pa", label: "ਪੰਜਾਬੀ" },
  { code: "as", label: "অসমীয়া" },
];

const ROLES = [
  "student",
  "parent",
  "manager",
  "owner",
  "mess",
  "transporter",
  "institution",
  "huma",
  "director",  
  "admin",
  "coordinator",
  "trustee",
];

const HAFHHome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userId = authState._id || null;
  const userCode = authState.think5whyscode || "";
  const userName = authState?.user?.name || "User";

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [theme, setTheme] = useState("DARK");
  const currentTheme = THEMES[theme];

  // COMMON THEME HELPERS
  const cardClass = `
    ${currentTheme.card}
    ${currentTheme.text}
    border
    ${currentTheme.border}
    rounded-2xl
    p-5
    shadow-lg
  `;

  const titleClass = `
    ${currentTheme.accent}
    text-lg
    font-semibold
  `;

  const paragraphClass = `
    ${currentTheme.subText}
    text-xs
    lg:text-sm
    leading-relaxed
  `;

  const buttonClass = `
    mt-8
    text-xs
    lg:text-sm
    px-4
    py-2
    rounded-xl
    transition-all
    ${currentTheme.button}
    ${currentTheme.buttonHover}
  `;

  const [activeTab, setActiveTab] = useState(null);

  useEffect(() => {
    const firstAccessibleTab = getDefaultTabForRole(userRole);

    if (firstAccessibleTab && !activeTab) {
      setActiveTab(firstAccessibleTab.id);
    }
  }, []);

  const [currentHostel] = useState("Srishti Hostel");
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [userRole, setUserRole] = useState("student");

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const [moduleWarning, setModuleWarning] = useState(false);

  const hasModuleAccess = (user, moduleCode) => {
    if (!user?.modules || user.modules.length === 0) return false;
    // console.log(hasModuleAccess, moduleCode);

    return user.modules.some(
      (m) =>
        m.moduleCode === moduleCode 
    );
  };

  useEffect(() => {
    const moduleCode = "HAFH";
    const allowed = hasModuleAccess(authState.user, moduleCode);
    // console.log(moduleCode, allowed);
    if (!allowed) {
    setModuleWarning(true);
    setActiveTab("onboarding");
    } else {
      setModuleWarning(false);
    }
  }, [authState]);

  const { markAttendance } = useAttendance(currentHostel);

  const t = (key) => hafhTranslations[selectedLanguage]?.[key] || key;
  const hostelTotalPoints = useMemo(() => students.reduce((sum, s) => sum + s.score, 0), [students]);

  const [emergencies, setEmergencies] = useState(SEED_EMERGENCIES);
  const [hasEmergencyAlert, setHasEmergencyAlert] = useState(false);
  const ENABLE_DEV_ALERTS = false;

  const SHOULD_RUN_ALERTS =
    process.env.NODE_ENV === "production" ||
    ENABLE_DEV_ALERTS;

  const triggerEmergencyAlert = () => {
    navigator.vibrate?.([200, 100, 200]);
  };

  useEffect(() => {
    if (!SHOULD_RUN_ALERTS) return;
    const interval = setInterval(() => {
      setHasEmergencyAlert(true);
      setEmergencies((prev) => [
        {
          id: Date.now(),
          role: "student",
          userName: "Live Emergency",
          roomNumber: `A-${Math.floor(Math.random() * 500)}`,
          reason: "AI detected unusual hostel behavior",
          timestamp: new Date().toLocaleTimeString(),
          geo: "19.0760, 72.8777",
          severity:
            Math.random() > 0.5
              ? "critical"
              : "high",
        },

        ...prev.slice(0, 19),
      ]);

      setTimeout(() => {
        setHasEmergencyAlert(false);
      }, 5000);

    }, 15000);

    return () => clearInterval(interval);

  }, [SHOULD_RUN_ALERTS]);

  useEffect(() => {
    if (!hasEmergencyAlert) return;
    triggerEmergencyAlert();
    if (
      "Notification" in window &&
      Notification.permission === "granted"
    ) {
      new Notification("🚨 Emergency Detected", {
        body: "Agentic AI detected a hostel emergency.",
      });
    }

  }, [hasEmergencyAlert]);

  useEffect(() => {
    if ("Notification" in window) {
      Notification.requestPermission();
    }
  }, []);

  const tabs = [
    { id: "owner", label: t("owner"), icon: faBuilding, roles: ["owner"] },
    { id: "manager", label: t("manager"), icon: faUserTie, roles: ["manager"] },
    { id: "student", label: t("student"), icon: faUserGraduate, roles: ["student"] },
    { id: "parent", label: t("parent"), icon: faUsers, roles: ["parent"] },
    { id: "mess", label: t("mess"), icon: faUtensils, roles: ["mess"] },
    { id: "transporter", label: t("transporter"), icon: faBus, roles: ["transporter"] }, 
    { id: "institution", label: t("institution"), icon: faSchool, roles: ["institution"] },  
    { id: "trustee", label: t("trustee"), icon: faPeopleGroup, roles: ["trustee"] },  
    { id: "admin", label: t("admin"), icon: faShieldAlt, roles: ["admin"] },
    { id: "dbdirector", label: t("dbDirector"), icon: faSitemap, roles: ["director"] },
    { id: "coordinator", label: t("coordinator"), icon: faClipboardCheck, roles: ["coordinator"] },
    { id: "huma", label: t("huma"), icon: faChalkboardUser, roles: ["huma"] },  
    { id: "signal", label: t("signal"), icon: faBrain, roles: ["student", "parent", "manager", "owner", "mess", "admin", "coordinator", "director", "huma", "trustee", "transporter", "institution"] },
    { id: "emergency", label: t("emergency"), icon: faTriangleExclamation, roles: ["student", "parent", "manager", "owner", "mess", "admin", "institution", "trustee", "huma", "director", "coordinator"] },
    { id: "FuelStash", label: t("FuelStash"), icon: faBolt, roles: ["student", "parent", "manager", "owner", "mess", "admin", "coordinator", "director", "huma", "trustee", "transporter", "institution"] },    
    { id: "BOK", label: t("BOK"), icon: faBookOpen, roles: ["student", "parent", "manager", "owner", "mess", "admin", "coordinator", "director", "huma", "trustee", "transporter", "institution"] },    
    { id: "Marketplace", label: t("Marketplace"), icon: faBolt, roles: ["student", "parent", "manager", "owner", "mess", "admin", "coordinator", "director", "huma", "trustee", "transporter", "institution"] },    
    { id: "1996+", label: t("1996+"), icon: faBolt, roles: ["student", "parent", "manager", "owner", "mess", "admin", "coordinator", "director", "huma", "trustee", "transporter", "institution"] },    
    { id: "hafhUpdates", label: t("hafhUpdates"), icon: faNewspaper, roles: ["student", "parent", "manager", "owner", "mess", "admin", "coordinator", "director", "huma", "trustee", "transporter", "institution"] },    
    { id: "onboarding", label: t("onboarding"), icon: faClipboardList, roles: ["student", "parent", "manager", "owner", "mess", "admin", "coordinator", "director", "huma", "trustee", "transporter", "institution"] },
    { id: "hafhAbout", label: t("hafhAbout"), icon: faCircleInfo, roles: ["student", "parent", "manager", "owner", "mess", "admin", "coordinator", "director", "huma", "trustee", "transporter", "institution"] },  
  ];

  const visibleTabs = tabs.filter((t) => t.roles.includes(userRole));

  const getDefaultTabForRole = (role) => {
    return tabs.find(
      (tab) =>
        tab.roles.includes(role) &&
        canAccessTab(tab.roles)
    );
  };

  const handleRoleChange = (role) => {
    setUserRole(role);

    const matchedTab = getDefaultTabForRole(role);

    if (matchedTab) {
      setActiveTab(matchedTab.id);
    }
  };

  const allowedRoles = useMemo(() => {
    if (!authState?.user?.modules) return [];

    return authState.user.modules.flatMap(m => m.roleCodes || []);
  }, [authState]);

  const canAccessTab = (tabRoles = []) => {
    if (!tabRoles?.length) return false;

    return tabRoles.some(role => allowedRoles.includes(role.toUpperCase()));
  };

  return (
    <div className={`min-h-screen flex flex-col ${currentTheme.bg} ${currentTheme.text} font-sans`}>
      <header className="sticky top-0 z-50 bg-gray-900 border-b border-gray-700 px-4 py-4 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="flex flex-col gap-1">
            <div className="tetx-lg lg:text-2xl font-bold text-yellow-200">HAFH - {t("hafh")}</div>
            <div className="text-[10px] text-gray-100 mt-0.5">{t("subTitle")}</div>
          </div>
          <div className="flex items-center gap-3 mt-2">
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="bg-gray-800 border border-gray-600 text-white rounded-lg px-1 py-1 text-xs h-8"
            >
              {languages.map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
            <select
              value={userRole}
              onChange={(e) => handleRoleChange(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-1 py-1 text-xs h-8"
            >
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {role.toUpperCase()}
                </option>
              ))}
            </select>
            <select
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              className="bg-gray-800 border border-gray-600 text-white rounded-lg px-1 py-1 text-xs h-8"
            >
              {Object.entries(THEMES).map(([key, value]) => (
                <option key={key} value={key}>
                  {value.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs lg:text-sm text-teal-300">{userName}</div>
              <button
                onClick={handleLogout}
                className="w-8 mt-2 px-2 py-1 rounded-md bg-red-600 hover:bg-red-700 transition"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="w-8 px-2 py-1 rounded-full border border-gray-700 text-sm text-gray-200 hover:bg-gray-800"
              >
                🔐 
              </button>
            </>
          )}
        </div>
        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden w-8 h-8 flex items-center justify-center text-2xl text-teal-300"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

{/* <div className="text-xs text-gray-300 bg-gray-900 p-3 rounded">
  <pre>{JSON.stringify(authState?.user, null, 2)}</pre>
</div>     */}
{/* {moduleWarning ? "yes modulewar" : "no module wa"}   */}
      {moduleWarning && (
        <div className="flex flex-col space-2-2 mt-4 p-2">
          <div className="w-full flex justify-center p-4">
            <div className="w-full max-w-2xl bg-yellow-900/20 border border-yellow-500 text-yellow-200 rounded-2xl p-6 shadow-lg">
              <div className="text-xl font-bold flex items-center gap-2">
                🚧 HAFH Access Pending
              </div>
              <div className="mt-3 text-sm leading-relaxed">
                Your account is currently not onboarded for:
                <span className="font-bold text-yellow-300">
                  {" "}HAFH - HomeAway From Home
                </span>
              </div>
              <div className="mt-4 bg-black/20 rounded-xl p-4">
                <div className="font-semibold text-yellow-300 mb-2">
                  Once onboarded, you will get access to:
                </div>

                <ul className="space-y-2 text-sm [&_li]:text-gray-200">
                  <li>✅ Hostel / Accommodation Management</li>
                  <li>✅ Attendance & Daily Monitoring</li>
                  <li>✅ Emergency & Safety Features</li>
                  <li>✅ Communication & Notices</li>
                  <li>✅ Reports, Activities & Tracking</li>
                </ul>
              </div>

              <div className="mt-5">
                <div className="font-semibold text-yellow-300 mb-2">
                  Quick Onboarding Steps
                </div>

                <div className="space-y-2 text-sm">
                  <div>1️⃣ Open the onboarding form</div>
                  <div>2️⃣ Fill basic profile & hostel details</div>
                  <div>3️⃣ Submit required information</div>
                  <div>4️⃣ Wait for approval / activation</div>
                </div>
              </div>

              {/* FOOTER NOTE */}
              <div className="mt-5 text-xs text-yellow-300 leading-relaxed">
                ⏱ Estimated onboarding time: 3–5 minutes <br />
                📌 Some features may become available only after verification.
              </div>

              {/* CTA */}
              <button
                onClick={() => setActiveTab("onboarding")}
                className="mt-6 w-full sm:w-auto px-5 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-medium transition"
              >
                Click Here to Start Self Onboarding
              </button>
            </div>
          </div>
          <div>
            {activeTab === "onboarding" && (<HAFHOnboarding t={t} selectedLanguage={selectedLanguage} role={userRole} isNew={true}/>)}
          </div>
        </div>
      )}

      {!moduleWarning && (
        <>
          <div
            className={`
              grid grid-cols-5 lg:grid-cols-11 gap-2 p-2
              ${currentTheme.card}
              border-b
              ${currentTheme.border}
            `}
          >
            {tabs
              .filter((t) => canAccessTab(t.roles))
              .map((t) => {
                const isActive = activeTab === t.id;

                return (
                  <button
                    key={t.id}
                    onClick={() => setActiveTab(t.id)}
                    className={`
                      relative min-w-[80px]
                      flex flex-col items-center justify-center
                      py-3 px-2 rounded-2xl
                      text-xs font-medium
                      transition-all duration-200

                      ${isActive
                        ? `${currentTheme.accentBg} text-white shadow-lg ${currentTheme.glow}`
                        : `${currentTheme.card} ${currentTheme.text} border ${currentTheme.border} ${currentTheme.hover}`
                      }

                      ${
                        t.id === "emergency" && hasEmergencyAlert
                          ? "animate-pulse"
                          : ""
                      }
                    `}
                  >
                    <FontAwesomeIcon
                      icon={t.icon}
                      className={`
                        text-lg mb-1
                        ${isActive ? "text-white" : currentTheme.accent}
                      `}
                    />

                    <span>{t.label}</span>

                    {t.id === "emergency" && emergencies.length > 0 && (
                      <div
                        className={`
                          absolute -top-1 -right-1
                          ${currentTheme.danger}
                          text-[10px]
                          rounded-full
                          h-5 w-5
                          flex items-center justify-center
                          font-bold
                        `}
                      >
                        {emergencies.length}
                      </div>
                    )}
                  </button>
                );
              })}
          </div>

          {/* MAIN CONTENT */}

          <main className="flex-1 p-4 pb-20 space-y-6">

            {/* OWNER */}
            {activeTab === "owner" && (
              <div
                className={`
                  rounded-2xl p-4
                  ${currentTheme.card}
                  ${currentTheme.border}
                  border
                `}
              >
                <HAFHOwner
                  t={t}
                  selectedLanguage={selectedLanguage}
                  currentHostel={null}
                  role={userRole}
                  currentTheme={currentTheme}
                />
              </div>
            )}

            {/* MANAGER */}
            {activeTab === "manager" && (
              <div className={`${currentTheme.card} border ${currentTheme.border} rounded-2xl p-4`}>
                <HAFHManager
                  t={t}
                  selectedLanguage={selectedLanguage}
                  role={userRole}
                  currentTheme={currentTheme}
                />
              </div>
            )}

            {/* STUDENT */}
            {activeTab === "student" && (
              <div className={`${currentTheme.card} border ${currentTheme.border} rounded-2xl p-4`}>
                <HAFHStudent
                  t={t}
                  selectedLanguage={selectedLanguage}
                  currentHostel={null}
                  role={userRole}
                  currentTheme={currentTheme}
                />
              </div>
            )}

            {/* PARENT */}
            {activeTab === "parent" && (
              <div className={`${currentTheme.card} border ${currentTheme.border} rounded-2xl p-4`}>
                <HAFHParent
                  t={t}
                  selectedLanguage={selectedLanguage}
                  userRole={userRole}
                  currentTheme={currentTheme}
                />
              </div>
            )}

            {/* MESS */}
            {activeTab === "mess" && (
              <div className={`${currentTheme.card} border ${currentTheme.border} rounded-2xl p-4`}>
                <HAFHMess
                  t={t}
                  selectedLanguage={selectedLanguage}
                  userRole={userRole}
                  currentTheme={currentTheme}
                />
              </div>
            )}

            {/* ADMIN */}
            {activeTab === "admin" && (
              <div className={`${currentTheme.card} border ${currentTheme.border} rounded-2xl p-4`}>
                <HAFHAdminPanel
                  t={t}
                  selectedLanguage={selectedLanguage}
                  role={userRole}
                  currentTheme={currentTheme}
                />
              </div>
            )}

            {/* SIGNAL */}
            {activeTab === "signal" && (
              <div className={`${currentTheme.card} border ${currentTheme.border} rounded-2xl p-4`}>
                <HAFHAI
                  t={t}
                  selectedLanguage={selectedLanguage}
                  role={userRole}
                  currentTheme={currentTheme}
                />
              </div>
            )}

            {/* INSTITUTION */}
            {activeTab === "institution" && (
              <div className={`${currentTheme.card} border ${currentTheme.border} rounded-2xl p-4`}>
                <HAFHInstitution
                  t={t}
                  selectedLanguage={selectedLanguage}
                  role={userRole}
                  currentTheme={currentTheme}
                />
              </div>
            )}

            {/* DIRECTOR */}
            {activeTab === "dbdirector" && (
              <div className={`${currentTheme.card} border ${currentTheme.border} rounded-2xl p-4`}>
                <HAFHDirector
                  t={t}
                  selectedLanguage={selectedLanguage}
                  role={userRole}
                  currentTheme={currentTheme}
                />
              </div>
            )}

            {/* HUMA */}
            {activeTab === "huma" && (
              <div className={`${currentTheme.card} border ${currentTheme.border} rounded-2xl p-4`}>
                <HAFHHuma
                  t={t}
                  selectedLanguage={selectedLanguage}
                  role={userRole}
                  currentTheme={currentTheme}
                />
              </div>
            )}

            {/* TRANSPORTER */}
            {activeTab === "transporter" && (
              <div className={`${currentTheme.card} border ${currentTheme.border} rounded-2xl p-4`}>
                <HAFHTransporter
                  t={t}
                  selectedLanguage={selectedLanguage}
                  role={userRole}
                  currentTheme={currentTheme}
                />
              </div>
            )}

            {/* TRUSTEE */}
            {activeTab === "trustee" && (
              <div className={`${currentTheme.card} border ${currentTheme.border} rounded-2xl p-4`}>
                <HAFHTrustee
                  t={t}
                  selectedLanguage={selectedLanguage}
                  role={userRole}
                  currentTheme={currentTheme}
                />
              </div>
            )}

            {/* COORDINATOR */}
            {activeTab === "coordinator" && (
              <div className={`${currentTheme.card} border ${currentTheme.border} rounded-2xl p-4`}>
                <HAFHCoordinator
                  t={t}
                  selectedLanguage={selectedLanguage}
                  role={userRole}
                  currentTheme={currentTheme}
                />
              </div>
            )}

            {/* FUEL STASH */}
            {activeTab === "FuelStash" && (
              <div className={cardClass}>
                <h2 className={titleClass}>
                  {t("FuelStash")}
                </h2>
                <div className="space-y-3 mt-3">
                  <p className={paragraphClass}>
                    ⚡Fuel Stash is your personal energy reserve inside Think5Whys for growth, rewards, progress, and
                    everyday ecosystem activities.
                  </p>
                  <p className={paragraphClass}>
                    🎯 Earn Fuel through learning, discipline,  consistency, achievements, participation, contributions, and positive actions.
                  </p>
                  <p className={paragraphClass}>
                    🚀 Use your Fuel for transformational ecosystem experiences, rewards, ecosystem access, services, activities, and future growth opportunities.
                  </p>
                  <p className={paragraphClass}>
                    📈 Track your Fuel history, streaks, rewards, boosts, and smart activity insights across your transformation journey.
                  </p>
                </div>

                <button onClick={() => navigate("/wallet-home")} className={buttonClass}>
                  ⚡Open Fuel Stash
                </button>
              </div>
            )}

            {activeTab === "1996+" && (
              <div className={cardClass}>
                <h2 className={titleClass}>
                  {t("1996+")}
                </h2>

                <div className="space-y-3 mt-3">
                  <p className={paragraphClass}>
                    🚀 Built for creators, learners, future founders, and digital-first generations navigating an AI-powered world.
                  </p>

                  <p className={paragraphClass}>
                    ⚡ Learn practical skills, use AI productively, join execution teams, and explore startup and earning opportunities.
                  </p>

                  <p className={paragraphClass}>
                    📈 Progress through continuous learning, capability building, smart certifications, and measurable growth journeys.
                  </p>

                  <p className={paragraphClass}>
                    🧠 Develop stronger habits, better decision-making, and Kaizen-based improvement through a transformation-focused ecosystem.
                  </p>
                </div>

                <button
                  onClick={() => navigate("/ninetysix")}
                  className={buttonClass}
                >
                  ⚡ Know More
                </button>
              </div>
            )}

            {/* BOK */}
            {activeTab === "BOK" && (
              <div className={cardClass}>
                <h2 className={titleClass}>
                  {t("GenZ+ BOK")}
                </h2>

                <div className="space-y-3 mt-3">
                  <p className={paragraphClass}>
                    ⚡ BOK (Body Of Knowledge) is your launchpad beyond classrooms, random internet content, and passive GenAI consumption — built for Gen Z, Gen Alpha, and future digital creators, operators, and transformation leaders.
                  </p>
                  <p className={paragraphClass}>
                    🌍 This is where knowledge transforms into real-world capability, digital confidence, ecosystem thinking, certifications, earning opportunities, and startup journeys.
                  </p>
                  <p className={paragraphClass}>
                    🧠 Learn practical process orientation, AI, automation, data-centric thinking, digital operations, and real-world ecosystem skills through immersive learning and smart assessments.
                  </p>
                  <p className={paragraphClass}>
                    🎯 Build certifications, practical capabilities, behavioral discipline, and job-ready expertise that supports modern careers, freelancing, entrepreneurship, and ecosystem opportunities.
                  </p>
                  <p className={paragraphClass}>
                    🚀 Start your own certified Digital Boutique Advisor journey individually or with friends in your geographical area and grow as a transformation partner within the Think5Whys ecosystem.
                  </p>
                  <p className={paragraphClass}>
                    📈 Track your learning progress, certifications, assessments, growth pathways, achievements, and continuous transformation journey from knowledge to earning.
                  </p>
                </div>

                <button
                  onClick={() => navigate("/lnd")}
                  className={buttonClass}
                >
                  📘 Open BOK Learning Ecosystem
                </button>

              </div>
            )}

            {/* PROFILE */}
            {activeTab === "onboarding" && (
              <div className={`${currentTheme.card} border ${currentTheme.border} rounded-2xl p-4`}>
                <HAFHOnboarding
                  t={t}
                  selectedLanguage={selectedLanguage}
                  role={userRole}
                  isNew={false}
                  currentTheme={currentTheme}
                />
              </div>
            )}
            
            {/* About */}
            {activeTab === "hafhAbout" && (
              <div className={`${currentTheme.card} border ${currentTheme.border} rounded-2xl p-4`}>
                <HAFHAbout
                  t={t}
                  selectedLanguage={selectedLanguage}
                  role={userRole}
                  currentTheme={currentTheme}
                />
              </div>
            )}

            {/* Updates */}
            {activeTab === "hafhUpdates" && (
              <div className={`${currentTheme.card} border ${currentTheme.border} rounded-2xl p-4`}>
                <HAFHUpdates
                  t={t}
                  selectedLanguage={selectedLanguage}
                  currentTheme={currentTheme}
                />
              </div>
            )}

            {/* EMERGENCY */}
            {activeTab === "emergency" && (
              <div className="space-y-4">
                {emergencies.map((e) => (
                  <div
                    key={e.id}
                    className={`
                      rounded-2xl p-4 border
                      ${currentTheme.card}
                      ${currentTheme.border}
                      shadow-lg
                    `}
                  >
                    <div className="flex justify-between items-start">

                      <div>
                        <div className={`${currentTheme.accent} font-bold`}>
                          {e.role.toUpperCase()}
                        </div>

                        <div className={`text-lg font-semibold ${currentTheme.text}`}>
                          {e.userName}
                        </div>
                      </div>

                      <div
                        className={`
                          px-3 py-1 rounded-xl text-xs font-bold
                          ${currentTheme.danger}
                        `}
                      >
                        {e.severity}
                      </div>
                    </div>

                    {e.roomNumber && (
                      <div className={`${currentTheme.subText} text-sm mt-3`}>
                        Room: {e.roomNumber}
                      </div>
                    )}

                    {e.geo && (
                      <div className={`${currentTheme.subText} text-xs mt-1`}>
                        Geo: {e.geo}
                      </div>
                    )}

                    <div className={`${currentTheme.text} mt-4`}>
                      {e.reason || "No reason provided"}
                    </div>

                    <div className={`${currentTheme.subText} text-xs mt-4`}>
                      {e.timestamp}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </main>
        </>
      )}
      <footer className="bg-gray-900 border-b border-gray-700 py-4">
          <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
            <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
            <div className="flex flex-wrap gap-4 text-sm">
              <Link to="/about-us" className="hover:text-teal-300 text-gray-200">About</Link>
              <Link to="/privacy-policy" className="hover:text-teal-300 text-gray-200">Privacy Policy</Link>
              <Link to="/terms-of-use" className="hover:text-teal-300 text-gray-200">Terms of Use</Link>
              <Link to="/contact-us" className="hover:text-teal-300 text-gray-200">Contact</Link>
            </div>
          </div>
        </footer>
    </div>
  );
};

export default HAFHHome;